import React, {
  useEffect,
  useState,
  Suspense,
  useCallback,
  lazy,
  useMemo,
  useRef,
} from "react";
import {
  Box,
  Typography,
  Alert,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Collapse,
  Avatar,
  CircularProgress,
  Paper,
  Card,
  CardContent,
  Chip,
  Stack,
  IconButton,
  Tooltip,
  Badge,
  Grid,
  alpha,
  Fade,
  Slide
} from "@mui/material";
import {
  Dashboard as DashboardIcon,
  ExpandLess,
  ExpandMore,
  Logout as LogoutIcon,
  Menu as MenuIcon,
  ChevronLeft as ChevronLeftIcon,
  Settings,
  AccountCircle,
  Business,
  Brightness4,
  Brightness7,
  Home,
  TrendingUp,
  PrecisionManufacturing,
  Memory,
  Assessment,
  WarningAmber
} from "@mui/icons-material";
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import * as MuiIcons from "@mui/icons-material";

import { useAuthContext } from "../contexts/AuthContext";
import { useLocation, useNavigate } from "react-router-dom";
import { useAppTheme } from "../contexts/ThemeContext";

import api from "../services/api";
import LanguageSelector from "../components/LanguageSelector";
import { useTranslation } from "react-i18next";
import IndustrialNotifications from "./Industrial/IndustrialNotifications";
import useProtectedLogo from "../components/useProtectedLogo";
import DynamicComponentLoader from "../components/DynamicComponentLoader";
import HubLicenseMappings from "./HubLicenseMappings";
import { flattenScreens, permittedScreen, permittedNavigationScreen } from "../utils/screenRegistry";

const NotFound = lazy(() => import("./NotFound"));

// O relógio repinta apenas a si mesmo: não remonta o formulário a cada segundo.
const DashboardClock = React.memo(() => {
  const { i18n } = useTranslation("salon");
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);
  return now.toLocaleString(i18n.resolvedLanguage || "pt-BR", {
    day: "2-digit", month: "2-digit", year: "numeric",
    hour: "2-digit", minute: "2-digit", second: "2-digit"
  });
});

/**
 * ✅ Helpers seguros para evitar crash de JSON.parse
 * - se não for JSON, retorna fallback
 * - se for JSON inválido, retorna fallback
 */
const safeJsonParse = (value, fallback = null) => {
  if (value == null) return fallback;
  const s = String(value).trim();
  if (!s) return fallback;

  if (!(s.startsWith("{") || s.startsWith("["))) return fallback;

  try {
    return JSON.parse(s);
  } catch {
    return fallback;
  }
};

/**
 * Dashboard Operacional / CRM de Fábrica
 * Design industrial, focado em alta densidade de dados e clareza visual.
 */
const Dashboard = ({ initialScreen }) => {
  const { t: tsalon } = useTranslation("salon");
  const { user, logout, permissions } = useAuthContext();
  const { palette, updatePalette, saveTheme } = useAppTheme();
  const theme = useTheme();
  const isDownLg = useMediaQuery(theme.breakpoints.down("lg"));
  const isDownMd = useMediaQuery(theme.breakpoints.down("md"));
  const location = useLocation();
  const navigate = useNavigate();

  const [greeting, setGreeting] = useState("");
  const [menu, setMenu] = useState([]);
  const [openMenus, setOpenMenus] = useState({});
  const [isCollapsed, setIsCollapsed] = useState(() => window.innerWidth < 1280);
  const [activeComponent, setActiveComponent] = useState(null);
  const [screenName, setScreenName] = useState("");
  // Protege a ficha aberta contra reabertura da página inicial após refresh de permissões.
  const initialNavigationKeyRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [darkMode, setDarkMode] = useState(false);
  const [selectedEntidade, setSelectedEntidade] = useState(null);
  const [selectedUnidade, setSelectedUnidade] = useState(null);
  const [entityLogo, setEntityLogo] = useState("/images/logodash.png");
  const [logoBroken, setLogoBroken] = useState(false);

  const industrialVisible = flattenScreens(permissions).some(p => /produtosindustria|ppcp|expedicao|operacaoindustrial|configuracaoindustrial|dashboardgestaoindustrial/i.test(p.telaNome || ""));
  const perfilId = user?.perfilId;
  const versaoId = user?.versaoSistemaId;

  const memoizedPermissions = useMemo(() => flattenScreens(permissions || []), [permissions]);

  // O logo e o contexto visual devem refletir a sessão autenticada, não um
  // identificador antigo em localStorage. A API continua sendo a autoridade.
  const sessionEntityId = user?.entidadeId || user?.EntidadeId || null;
  const sessionUnitId = user?.unidadeId || user?.UnidadeId || null;
  const isRootSession = String(user?.tipoUsuario || user?.TipoUsuario || "").toLowerCase() === "root";
  // Root pode selecionar a unidade sem a incluir no JWT; outros perfis
  // SEMPRE obtêm o escopo da sessão e não do armazenamento do navegador.
  const rootUnit = isRootSession ? safeJsonParse(localStorage.getItem("selectedUnidade"), null) : null;
  const rootEntity = isRootSession ? safeJsonParse(localStorage.getItem("selectedEntidade"), null) : null;
  const activeUnitId = sessionUnitId || (isRootSession ? rootUnit?.id || rootUnit?.Id : null);
  const activeEntityId = sessionEntityId || (isRootSession ? rootEntity?.id || rootEntity?.Id : null);
  const protectedUnitLogo = useProtectedLogo(activeUnitId ? "unidades" : "entidades", activeUnitId || activeEntityId);
  useEffect(() => { if (protectedUnitLogo) setLogoBroken(false); }, [protectedUnitLogo]);

  useEffect(() => {
    const storedEntity = safeJsonParse(localStorage.getItem("selectedEntidade"), null);
    const storedUnit = safeJsonParse(localStorage.getItem("selectedUnidade"), null);
    const entityId = activeEntityId;
    const unitId = activeUnitId;
    const chosenEntity = entityId && String(storedEntity?.id || storedEntity?.Id).toLowerCase() === String(entityId).toLowerCase()
      ? storedEntity : entityId ? { id: entityId } : null;
    const chosenUnit = unitId && String(storedUnit?.id || storedUnit?.Id).toLowerCase() === String(unitId).toLowerCase()
      ? storedUnit : unitId ? { id: unitId } : null;
    setSelectedEntidade(chosenEntity);
    setSelectedUnidade(chosenUnit);
    setLogoBroken(false);
    setEntityLogo("/images/logodash.png");
    if (!entityId) return undefined;
    let active = true;
    const getData = response => response?.data?.data || response?.data || {};
    // Metadados do banco autorizados pelo JWT, nunca construir nomes a partir dos IDs.
    api.get(`/api/Entidade/${entityId}`)
      .then(response => {
        if (!active) return;
        const data = getData(response);
        setSelectedEntidade(prev => prev && ({ ...prev, nome: data.nome || data.Nome || prev.nome }));
      }).catch(() => { /* entidade pode ser visível com autorização de foto mas sem acesso ao CRUD */ });
    if (unitId) {
      api.get(`/api/Unidade/${unitId}`).then(response => {
        if (!active) return;
        const data = getData(response);
        if (String(data.id || data.Id).toLowerCase() !== String(unitId).toLowerCase()) return;
        setSelectedUnidade(prev => ({ ...prev, nome: data.nome || data.Nome, fotoUrl: data.fotoUrl || data.FotoUrl }));
        if (data.entidadeNome || data.EntidadeNome) setSelectedEntidade(prev => ({ ...prev, nome: data.entidadeNome || data.EntidadeNome }));
        // Nunca expor URL S3 privada num <img>; useProtectedLogo carrega o ficheiro autorizado.
      }).catch(() => {});
    }
    return () => { active = false; };
  }, [activeEntityId, activeUnitId]);

  useEffect(() => {
    if (isDownLg) setIsCollapsed(true);
  }, [isDownLg]);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000);
    return () => clearInterval(timer);
  }, []);

  const getPersonalizedGreeting = () => {
    const hour = currentTime.getHours();
    const userName = user?.nome?.split(' ')[0] || "Operador";

    if (hour < 12) return `Turno da Manhã - Olá, ${userName}`;
    if (hour < 18) return `Turno da Tarde - Olá, ${userName}`;
    return `Turno da Noite - Olá, ${userName}`;
  };

  const getMotivationalMessage = () => {
    const messages = [
      "Sistemas operacionais normais. Pronto para produção.",
      "Monitoramento de planta ativo. Boa jornada.",
      "Verifique os relatórios de fechamento de turno.",
      "Atenção aos indicadores de performance (OEE)."
    ];

    const hour = currentTime.getHours();
    if (hour < 12) return messages[0];
    if (hour < 15) return messages[1];
    if (hour < 18) return messages[2];
    return messages[3];
  };

  useEffect(() => {
    const hour = new Date().getHours();
    const greetingText =
      hour < 12 ? "Bom dia" : hour < 18 ? "Boa tarde" : "Boa noite";
    setGreeting(greetingText);
  }, []);

  // A autorização da navegação provém exclusivamente da lista efetiva da API;
  // formulários de CRUD usam a permissão da sua listagem, sem bypass por papel.
  const canOpenScreen = useCallback(
    (name) => permittedNavigationScreen(memoizedPermissions, name),
    [memoizedPermissions]
  );

  const handleNavigation = useCallback((screen, membro = null, navigationData = null) => {
    const root = String(user?.tipoUsuario || "").toLowerCase() === "root";
    setScreenName(screen);
    if (screen === "HubLicenseMappings") {
      if (!root) {
        setActiveComponent(() => () => <Alert severity="error">Acesso exclusivo ao Root.</Alert>);
      } else {
        setActiveComponent(() => HubLicenseMappings);
      }
      return;
    }
    if (!(root && screen === "HubLicenseMappings") && !canOpenScreen(screen)) {
      setActiveComponent(() => () => <Alert severity="warning">Seu perfil não possui acesso a esta tela no contexto selecionado.</Alert>);
      return;
    }
    setActiveComponent(() => (props) => <DynamicComponentLoader component={screen} {...props} membro={membro} navigationData={navigationData} />);
  }, [canOpenScreen, user?.tipoUsuario]);

  useEffect(() => {
    // Permission revocation also closes a screen that was already open in the
    // current SPA without waiting for a reload or another menu click.
    if (!screenName) return;
    const root = String(user?.tipoUsuario || "").toLowerCase() === "root";
    if (!(root && screenName === "HubLicenseMappings") &&
        !canOpenScreen(screenName)) {
      setActiveComponent(() => () => <Alert severity="warning">Acesso revogado para esta tela no contexto atual.</Alert>);
    }
  }, [canOpenScreen, screenName, user?.tipoUsuario]);

  useEffect(() => {
    // A navegação automática só ocorre uma vez por contexto/entrada explícita.
    // Uma nova lista de permissões pode alterar a identidade de handleNavigation:
    // não remontar a tela corrente, pois isso descarta formulários e fotos não guardadas.
    const requestedScreen = initialScreen || location?.state?.screenName || null;
    const target = requestedScreen || (sessionUnitId && permittedScreen(memoizedPermissions, "BelezaAgenda")
      ? "BelezaAgenda" : null);
    if (!target) return;
    if (!canOpenScreen(target)) return;
    const scope = [activeEntityId || "", sessionUnitId || "", versaoId || ""].join(":");
    // location.key muda a cada clique Novo/Editar/Voltar, inclusive para o
    // mesmo componente. Atualizações de permissão não alteram essa chave:
    // não desmontam o formulário aberto nem reiniciam a tela padrão.
    const key = `${scope}|${requestedScreen ? `route:${location.key}:${target}` : "default"}`;
    if (initialNavigationKeyRef.current === key) return;
    initialNavigationKeyRef.current = key;
    handleNavigation(target, requestedScreen ? location?.state?.membro || null : null);
  }, [location?.key, location?.state, initialScreen, handleNavigation, sessionUnitId, activeEntityId, versaoId, memoizedPermissions, canOpenScreen]);

  useEffect(() => {
    const isRoot = String(user?.tipoUsuario || "").toLowerCase() === "root";
    if (memoizedPermissions.length === 0 && !isRoot) {
      setMenu([]);
      setLoading(false);
      return;
    }

    const modulosMap = {};

    memoizedPermissions.forEach((p) => {
      if (!modulosMap[p.moduloId]) {
        modulosMap[p.moduloId] = {
          moduloId: p.moduloId,
          moduloNome: p.moduloNome,
          moduloIcone: p.moduloIcone,
          moduloLabel: p.moduloLabel,
          telas: [],
        };
      }

      modulosMap[p.moduloId].telas.push({
        telaId: p.telaId,
        telaNome: p.telaNome,
        telaLabel: p.telaLabel,
        telaIcone: p.telaIcone,
        idTelaMae: p.idTelaMae,
      });
    });

    // Item técnico administrativo: nunca é concedido por ModuloVersao aos clientes.
    if (isRoot) {
      const administracao = Object.values(modulosMap).find(m =>
        String(m.moduloNome || m.moduloLabel || "").normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase() === 'administracao'
      );
      const target = administracao || {
        moduloId: '__root_administracao__',
        moduloNome: 'Administração',
        moduloIcone: 'AdminPanelSettings',
        moduloLabel: 'Administração',
        telas: []
      };
      if (!administracao) modulosMap[target.moduloId] = target;
      if (!target.telas.some(t => t.telaNome === 'HubLicenseMappings')) {
        target.telas.push({
          telaId: '__root_hub_licensing__',
          telaNome: 'HubLicenseMappings',
          telaLabel: 'Integração Lion Hub',
          telaIcone: 'CloudSync',
          idTelaMae: null
        });
      }
      setOpenMenus(prev => ({ ...prev, [target.moduloId]: true }));
    }

    const menuFinal = Object.values(modulosMap).map((modulo) => {
      // Permissões são por tela. Uma tela filha autorizada não deve desaparecer
      // apenas porque o pai não foi concedido; filhos de pais permitidos
      // aparecem indentados, sem duplicações nem ciclos.
      const id = value => String(value || "").toLowerCase();
      const byId = new Map(modulo.telas.map(t => [id(t.telaId), t]));
      const visited = new Set();
      const ordered = [];
      const visit = (tela, depth) => {
        const key = id(tela.telaId);
        if (visited.has(key)) return;
        visited.add(key);
        ordered.push({ ...tela, menuDepth: depth });
        modulo.telas.filter(t => id(t.idTelaMae) === key).forEach(child => visit(child, depth + 1));
      };
      modulo.telas
        .filter(t => !id(t.idTelaMae) || !byId.has(id(t.idTelaMae)))
        .forEach(t => visit(t, 0));
      modulo.telas.forEach(t => visit(t, 0)); // defensivo contra vínculos cíclicos
      return { ...modulo, telas: ordered };
    });

    setMenu(menuFinal);
    setLoading(false);
  }, [memoizedPermissions, user?.tipoUsuario]);

  useEffect(() => {
    const navigateIndustrial = event => {
      if (event.detail?.screen) handleNavigation(event.detail.screen, null, { pedidoId: event.detail.pedidoId });
    };
    window.addEventListener("industrial:navigate", navigateIndustrial);
    return () => window.removeEventListener("industrial:navigate", navigateIndustrial);
  }, [handleNavigation]);

  const toggleMenu = useCallback(
    (id) => {
      setOpenMenus((prev) => ({ ...prev, [id]: !prev[id] }));
    },
    []
  );

  const handleLogout = () => {
    ["token", "user", "permissions", "selectedEntidade", "selectedUnidade"].forEach((k) => localStorage.removeItem(k));
    logout();
    navigate("/login", { replace: true });
  };

  const renderedMenu = useMemo(() => {
    return menu.map(({ moduloId, moduloNome, moduloIcone, moduloLabel, telas }) => {
      const Icon = MuiIcons[moduloIcone] || DashboardIcon;
      const isOpen = openMenus[moduloId];

      return (
        <React.Fragment key={moduloId}>
          <ListItem
            button
            onClick={() => toggleMenu(moduloId)}
            sx={{
              borderRadius: 1, 
              mx: 1,
              mb: 0.5,
              backgroundColor: isOpen ? alpha(theme.palette.primary.main, 0.08) : 'transparent',
              borderLeft: isOpen ? `4px solid ${theme.palette.primary.main}` : '4px solid transparent',
              '&:hover': {
                backgroundColor: alpha(theme.palette.primary.main, 0.12),
                borderLeft: `4px solid ${theme.palette.primary.main}`
              },
              transition: 'all 0.2s ease-in-out'
            }}
          >
            <ListItemIcon sx={{ minWidth: 40 }}>
              <Icon sx={{ color: isOpen ? theme.palette.primary.main : theme.palette.text.secondary }} />
            </ListItemIcon>
            {!isCollapsed && (
              <>
                <ListItemText
                  primary={moduloLabel || moduloNome}
                  primaryTypographyProps={{
                    fontWeight: isOpen ? 'bold' : 'medium',
                    fontSize: '0.85rem',
                    textTransform: 'uppercase', 
                    letterSpacing: '0.5px'
                  }}
                />
                {isOpen ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
              </>
            )}
          </ListItem>
          <Collapse in={isOpen} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {telas.map(({ telaId, telaNome, telaLabel, telaIcone, menuDepth = 0 }) => {
                const TelaIcon = MuiIcons[telaIcone] || DashboardIcon;
                return (
                  <ListItem
                    button
                    key={telaId}
                    onClick={() => telaNome === 'HubLicenseMappings'
                      ? navigate('/dashboard/hub-licensing')
                      : handleNavigation(telaNome)}
                    sx={{
                      pl: (isCollapsed ? 2 : 5) + Math.min(menuDepth, 3) * 2,
                      borderRadius: 1,
                      mx: 1,
                      mb: 0.5,
                      borderLeft: '4px solid transparent',
                      '&:hover': {
                        backgroundColor: alpha(theme.palette.secondary.main, 0.08),
                        borderLeft: `4px solid ${theme.palette.secondary.main}`
                      },
                      transition: 'all 0.2s ease-in-out'
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <TelaIcon sx={{ color: theme.palette.text.secondary, fontSize: '1.1rem' }} />
                    </ListItemIcon>
                    {!isCollapsed && (
                      <ListItemText
                        primary={telaLabel || telaNome}
                        primaryTypographyProps={{
                          fontSize: '0.85rem',
                          color: theme.palette.text.primary
                        }}
                      />
                    )}
                  </ListItem>
                );
              })}
            </List>
          </Collapse>
        </React.Fragment>
      );
    });
  }, [menu, openMenus, handleNavigation, isCollapsed, theme, toggleMenu, navigate]);

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
        sx={{
          backgroundColor: '#0f172a' 
        }}
      >
        <Card elevation={0} sx={{ p: 4, borderRadius: 2, border: '1px solid #334155', backgroundColor: '#1e293b', color: '#fff' }}>
          <Box display="flex" alignItems="center" gap={3}>
            <CircularProgress size={32} sx={{ color: '#38bdf8' }} />
            <Typography variant="h6" sx={{ fontFamily: 'monospace' }}>INICIALIZANDO SISTEMA...</Typography>
          </Box>
        </Card>
      </Box>
    );
  }

  return (
    <Box
      display="flex"
      flexDirection="column"
      sx={{
        height: '100vh',
        minHeight: 0,
        overflow: 'hidden',
        backgroundColor: darkMode ? '#0f172a' : '#f1f5f9'
      }}
    >
      <Paper
        elevation={0}
        sx={{
          backgroundColor: '#1e293b', 
          color: '#f8fafc',
          position: 'relative',
          borderRadius: 0,
          borderBottom: '4px solid #38bdf8', 
          minHeight: { xs: 80, sm: 90 }, 
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center'
        }}
      >
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          px={{ xs: 1.25, sm: 2, lg: 3 }}
          py={{ xs: 1, sm: 1.25, lg: 1.5 }}
          position="relative"
          zIndex={1}
        >
          <Fade in timeout={800}>
            <Box display="flex" alignItems="center" gap={{ xs: 1, sm: 1.5, lg: 2 }} sx={{ minWidth: 0 }}>
              <Paper
                elevation={0}
                sx={{
                  p: 0.5,
                  borderRadius: 1,
                  backgroundColor: '#ffffff',
                  border: '1px solid #475569'
                }}
              >
                <img
                  src={logoBroken ? "/images/logodash.png" : (protectedUnitLogo || entityLogo)}
                  alt="Logo da unidade ativa"
                  onError={() => setLogoBroken(true)}
                  style={{
                    width: isDownMd ? 38 : 48,
                    height: isDownMd ? 38 : 48,
                    objectFit: 'contain',
                    display: 'block'
                  }}
                />
              </Paper>

              <Box>
                <Typography variant={isDownMd ? "subtitle1" : "h6"} fontWeight="bold" sx={{ letterSpacing: '1px', lineHeight: 1.2 }} noWrap>
                  {selectedUnidade?.nome || selectedEntidade?.nome || "CRM OPERACIONAL"}
                </Typography>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 0.5 }}>
                  <Badge color="success" variant="dot" />
                  <Typography variant="caption" sx={{ color: '#94a3b8', fontFamily: 'monospace' }}>
                    {selectedEntidade?.nome && selectedUnidade?.nome ? selectedEntidade.nome : "SISTEMA ONLINE"}
                  </Typography>
                </Stack>
              </Box>
            </Box>
          </Fade>

          <Slide direction="left" in timeout={1000}>
            <Box display="flex" alignItems="center" gap={{ xs: 1, sm: 1.5, lg: 3 }}>
              <Box textAlign="right" sx={{ display: { xs: 'none', md: 'block' } }}>
                <Typography variant="subtitle2" fontWeight="bold" sx={{ color: '#e2e8f0' }}>
                  {getPersonalizedGreeting()}
                </Typography>
                <Typography variant="caption" sx={{ color: '#94a3b8', display: 'block' }}>
                  {getMotivationalMessage()}
                </Typography>
                <Typography variant="caption" sx={{ color: '#38bdf8', fontFamily: 'monospace', fontWeight: 'bold' }}>
                  <DashboardClock />
                </Typography>
              </Box>



              <Box display="flex" alignItems="center" gap={1.5}>
                <LanguageSelector small dark />
                {industrialVisible && <IndustrialNotifications />}
                <Avatar
                  src={user?.fotoUrl}
                  alt={user?.nome}
                  sx={{
                    width: isDownMd ? 36 : 44,
                    height: isDownMd ? 36 : 44,
                    border: '2px solid #334155',
                    backgroundColor: '#0f172a'
                  }}
                >
                  <AccountCircle sx={{ fontSize: 30, color: '#64748b' }} />
                </Avatar>
              </Box>
            </Box>
          </Slide>
        </Box>
      </Paper>

      <Box
        display="flex"
        flex="1 1 auto"
        overflow="hidden"
        height="100%"
      >
        <Paper
          elevation={0}
          className="dashboard-sidebar custom-scrollbar"
          sx={{
            width: isCollapsed ? "64px" : "236px",
            position: "relative",
            backgroundColor: darkMode ? '#1e293b' : '#ffffff',
            borderRight: `1px solid ${darkMode ? '#334155' : '#e2e8f0'}`,
            display: "flex",
            flexDirection: "column",
            transition: 'width 0.3s ease',
            borderRadius: 0,
            zIndex: 2
          }}
        >
          <Box display="flex" justifyContent={isCollapsed ? "center" : "flex-end"} alignItems="center" p={1.5} borderBottom={`1px solid ${darkMode ? '#334155' : '#e2e8f0'}`}>
            <Tooltip title={isCollapsed ? "Expandir Menu" : "Recolher Menu"} placement="right">
              <IconButton
                onClick={() => setIsCollapsed(!isCollapsed)}
                size="small"
                sx={{
                  border: `1px solid ${darkMode ? '#475569' : '#cbd5e1'}`,
                  borderRadius: 1,
                  backgroundColor: darkMode ? '#0f172a' : '#f8fafc',
                  '&:hover': {
                    backgroundColor: theme.palette.primary.main,
                    color: '#fff'
                  }
                }}
              >
                {isCollapsed ? <MenuIcon fontSize="small" /> : <ChevronLeftIcon fontSize="small" />}
              </IconButton>
            </Tooltip>
          </Box>

          <Box flexGrow={1} overflow="auto" sx={{ py: 2 }} className="custom-scrollbar">
            <List disablePadding>{renderedMenu}</List>
          </Box>

          <Box
            p={2}
            sx={{
              borderTop: `1px solid ${darkMode ? '#334155' : '#e2e8f0'}`,
              backgroundColor: darkMode ? '#0f172a' : '#f8fafc'
            }}
          >
            <Button
              fullWidth={!isCollapsed}
              onClick={handleLogout}
              startIcon={<LogoutIcon />}
              variant="contained"
              color="error"
              disableElevation
              sx={{
                borderRadius: 1,
                fontWeight: 'bold',
                minWidth: isCollapsed ? 'auto' : '100%',
                px: isCollapsed ? 0 : 2
              }}
            >
              {!isCollapsed ? tsalon("logout").toUpperCase() : ""}
            </Button>
          </Box>
        </Paper>

        <Box
          flex={1}
          className="custom-scrollbar"
          sx={{
            backgroundColor: darkMode ? '#0f172a' : '#f1f5f9',
            overflowY: "auto",
            overflowX: "hidden",
            height: "100%",
            position: 'relative'
          }}
        >
          <Box p={{ xs: 1, sm: 1.5, md: 2, xl: 3 }} minHeight="100%" position="relative" zIndex={1}>
            {activeComponent ? (
              <Suspense
                fallback={
                  <Box display="flex" justifyContent="center" alignItems="center" height="60vh">
                    <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
                      <CircularProgress size={40} thickness={4} />
                      <Typography variant="button" color="textSecondary">Processando módulo...</Typography>
                    </Box>
                  </Box>
                }
              >
                <Fade in timeout={400}>
                  <Box sx={{ 
                    backgroundColor: darkMode ? '#1e293b' : '#ffffff', 
                    borderRadius: 1, 
                    boxShadow: darkMode ? 'none' : '0 1px 3px rgba(0,0,0,0.1)',
                    border: `1px solid ${darkMode ? '#334155' : '#e2e8f0'}`,
                    p: { xs: 1.25, sm: 1.5, md: 2, xl: 2.5 },
                    minHeight: { xs: 'calc(100vh - 126px)', md: 'calc(100vh - 145px)' }
                  }}>
                    {React.createElement(activeComponent)}
                  </Box>
                </Fade>
              </Suspense>
            ) : (
              <Fade in timeout={600}>
                <Box>
                  <Typography variant="h5" fontWeight="bold" sx={{ mb: 1, color: darkMode ? '#f8fafc' : '#1e293b' }}>
                    Visão Operacional
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 4, color: darkMode ? '#94a3b8' : '#64748b' }}>
                    Selecione um módulo no menu lateral ou acompanhe os indicadores em tempo real.
                  </Typography>

                  <Grid container spacing={{ xs: 1.5, md: 2 }}>
                    <Grid item xs={12}>
                      <Paper sx={{ p: 4, mt: 2, borderRadius: 1, textAlign: 'center', bgcolor: darkMode ? '#1e293b' : '#fff', border: `1px solid ${darkMode ? '#334155' : '#e2e8f0'}` }} elevation={0}>
                        <Business sx={{ fontSize: 60, color: darkMode ? '#475569' : '#cbd5e1', mb: 2 }} />
                        <Typography variant="h6" color="textSecondary">
                          {selectedEntidade?.nome || "Empresa / Entidade Operacional"}
                        </Typography>
                        {selectedUnidade && (
                          <Chip 
                            label={`Unidade de Operação: ${selectedUnidade.nome || "Unidade selecionada"}`} 
                            variant="outlined" 
                            size="small" 
                            sx={{ mt: 2, borderRadius: 1, fontFamily: 'monospace' }} 
                          />
                        )}
                      </Paper>
                    </Grid>
                  </Grid>
                </Box>
              </Fade>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default Dashboard;
